import React, { useState, useMemo } from "react";
import { Helmet } from "react-helmet";
import { faqData } from "../data/faqData";
import { FaSearch, FaPlus, FaMinus, FaWhatsapp, FaPhoneAlt } from "react-icons/fa";

const categories = [
  { id: "all", label: "All" },
  { id: "about", label: "About Our Properties" },
  { id: "residential", label: "Residential" },
  { id: "commercial", label: "Commercial" },
  { id: "locations", label: "Locations" },
  { id: "amenities", label: "Amenities" },
  { id: "pricing", label: "Pricing" },
];

const parseAnswer = (text) => {
  const blocks = text.split('\n\n');
  return blocks.map((block, i) => {
    if (block.startsWith('* ')) {
      const items = block.split('\n').filter(line => line.trim().startsWith('* '));
      return (
        <ul key={i} className="list-none space-y-2 mb-4 ml-2">
          {items.map((item, j) => (
            <li key={j} className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-blue-600 mt-2 flex-shrink-0" />
              <span className="text-gray-600 font-light leading-relaxed">{item.replace('* ', '')}</span>
            </li>
          ))}
        </ul>
      );
    }
    
    const lines = block.split('\n');
    return (
      <div key={i} className="mb-4 last:mb-0">
        {lines.map((line, j) => {
          if (line.startsWith('**') && line.endsWith('**')) {
            return <div key={j} className="text-blue-900 font-medium tracking-wide mt-4 mb-2">{line.replace(/\*\*/g, '')}</div>;
          }
          const parts = line.split(/(\*\*.*?\*\*)/g);
          return (
            <p key={j} className="text-gray-600 font-light leading-relaxed">
              {parts.map((part, k) => {
                if (part.startsWith('**') && part.endsWith('**')) {
                  return <strong key={k} className="text-blue-900 font-medium">{part.replace(/\*\*/g, '')}</strong>;
                }
                return part;
              })}
            </p>
          );
        })}
      </div>
    );
  });
};

const Faq = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");
  const [openItems, setOpenItems] = useState(new Set());

  const toggleItem = (id) => {
    setOpenItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const filteredData = useMemo(() => {
    return faqData.filter((item) => {
      const matchesCategory = activeCategory === "all" || item.categories.includes(activeCategory);
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = 
        item.question.toLowerCase().includes(searchLower) ||
        item.answer.toLowerCase().includes(searchLower) ||
        (item.keywords && item.keywords.toLowerCase().includes(searchLower));
      
      return matchesCategory && matchesSearch;
    });
  }, [searchTerm, activeCategory]);

  const handleWhatsappClick = () => {
    const message = encodeURIComponent("Hello Al Ghaith Properties, I have a question about your properties and would like some assistance.");
    window.open(`https://wa.me/971800442484?text=${message}`, "_blank");
  };

  const handlePhoneClick = () => {
    window.location.href = `tel:+971800442484`;
  };

  return (
    <div className="w-full bg-blue-50 text-gray-800 font-sans overflow-x-hidden min-h-screen pb-24" data-aos="zoom-in">
      <Helmet>
        <meta charSet="utf-8" />
        <title>Frequently Asked Questions | Al Ghaith Properties</title>
        <meta name="description" content="Find answers about Al Ghaith Properties, residential and commercial properties, locations, amenities and pricing." />
        <link rel="canonical" href="https://www.al-ghaith.ae/#/faq" />
      </Helmet>

      {/* HERO SECTION */}
      <section className="relative w-full pt-32 pb-16 px-6 bg-blue-900 flex flex-col items-center justify-center text-center border-b border-blue-800">
        <h1 className="text-3xl md:text-5xl lg:text-6xl font-light text-white mb-4 tracking-widest uppercase">
          Frequently Asked Questions
        </h1>
        <div className="w-16 md:w-24 h-[1px] bg-blue-400 mb-6" />
        <p className="text-lg md:text-xl text-white/80 tracking-widest font-light mb-4 uppercase">
          Questions? We've got answers.
        </p>
        <p className="text-sm md:text-base text-white/60 font-light max-w-2xl leading-relaxed">
          Find answers about Al Ghaith Properties, our residential and commercial spaces, locations, amenities and pricing.
        </p>

        {/* SEARCH BAR */}
        <div className="mt-12 w-full max-w-2xl relative">
          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400">
            <FaSearch />
          </div>
          <input
            type="text"
            className="w-full pl-12 pr-4 py-4 md:py-5 bg-white text-gray-900 rounded-sm shadow-xl border-none focus:ring-2 focus:ring-blue-600 transition-shadow text-base md:text-lg font-light placeholder-gray-400"
            placeholder="Search your question..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </section>

      {/* MAIN CONTENT */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          
          {/* CATEGORY FILTER */}
          <div className="flex overflow-x-auto hide-scrollbar gap-2 md:gap-4 mb-12 pb-4 justify-start md:justify-center border-b border-blue-200">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`whitespace-nowrap px-6 py-3 text-xs md:text-sm tracking-widest uppercase transition-all rounded-sm ${
                  activeCategory === cat.id 
                    ? "bg-blue-900 text-white shadow-md" 
                    : "bg-white text-gray-500 hover:bg-blue-100 hover:text-blue-900 border border-blue-100"
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>

          {/* SEARCH RESULT INDICATOR */}
          {searchTerm && (
            <div className="text-center mb-8 text-sm tracking-widest text-gray-500 uppercase">
              {filteredData.length} {filteredData.length === 1 ? 'question' : 'questions'} found
            </div>
          )}

          {/* FAQ ACCORDION */}
          {filteredData.length > 0 ? (
            <div className="space-y-4">
              {filteredData.map((item, index) => {
                const isOpen = openItems.has(item.id);
                const num = String(index + 1).padStart(2, '0');
                return (
                  <div 
                    key={item.id} 
                    className={`bg-white border transition-colors rounded-sm overflow-hidden ${isOpen ? 'border-blue-600 shadow-md' : 'border-blue-100 shadow-sm'}`}
                  >
                    <button
                      onClick={() => toggleItem(item.id)}
                      className="w-full text-left px-6 py-5 md:py-6 flex items-start md:items-center justify-between focus:outline-none group hover:bg-blue-50 transition-colors"
                      aria-expanded={isOpen}
                      aria-controls={`answer-${item.id}`}
                    >
                      <div className="flex items-start gap-4 md:gap-6 pr-6">
                        <span className="text-sm font-light tracking-widest text-blue-600 pt-1 md:pt-0">{num}</span>
                        <span className="text-base md:text-lg text-blue-900 font-light tracking-wide leading-snug group-hover:text-blue-700 transition-colors">
                          {item.question}
                        </span>
                      </div>
                      <div className="text-gray-400 group-hover:text-blue-600 transition-colors mt-1 md:mt-0 flex-shrink-0">
                        {isOpen ? <FaMinus size={16} /> : <FaPlus size={16} />}
                      </div>
                    </button>
                    
                    <div 
                      id={`answer-${item.id}`}
                      className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'}`}
                    >
                      <div className="px-6 pb-6 md:pl-16 pt-2 border-t border-blue-50 mx-6">
                        {parseAnswer(item.answer)}
                        
                        {/* Optional Pricing Disclaimer specifically for pricing category items */}
                        {item.categories.includes("pricing") && (
                          <div className="mt-6 p-4 bg-blue-50 border-l-2 border-blue-600 text-xs font-light text-blue-800 tracking-wide uppercase leading-relaxed">
                            Prices may vary depending on location, property type, size and availability. Please contact our team for current pricing.
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            /* EMPTY STATE */
            <div className="text-center py-16 bg-white border border-blue-100 rounded-sm shadow-sm">
              <h3 className="text-xl font-light tracking-widest text-blue-900 mb-4 uppercase">No questions found</h3>
              <p className="text-gray-500 font-light mb-8">Try searching with another keyword or contact our team directly.</p>
              <div className="flex flex-col sm:flex-row justify-center gap-4 px-6">
                <button 
                  onClick={handleWhatsappClick}
                  className="bg-[#25D366] text-white px-8 py-3 rounded-sm text-sm tracking-widest font-semibold hover:bg-[#1da851] transition-colors flex items-center justify-center gap-2"
                >
                  <FaWhatsapp size={18} /> WHATSAPP US
                </button>
                <button 
                  onClick={handlePhoneClick}
                  className="border border-blue-900 text-blue-900 px-8 py-3 rounded-sm text-sm tracking-widest font-semibold hover:bg-blue-50 transition-colors flex items-center justify-center gap-2"
                >
                  <FaPhoneAlt size={14} /> CALL US
                </button>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* CONTACT CTA */}
      <section className="py-20 px-6 bg-white border-t border-blue-200 text-center">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-light tracking-[0.1em] text-blue-900 mb-4 uppercase">
            Can't find what you're looking for?
          </h2>
          <p className="text-gray-600 mb-10 text-lg font-light">
            Our team is here to help.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <button 
              onClick={handleWhatsappClick}
              className="bg-blue-900 text-white px-8 py-4 rounded-sm text-sm tracking-widest font-semibold hover:bg-blue-800 transition-colors flex items-center justify-center gap-2 shadow-md"
            >
              <FaWhatsapp size={18} /> WHATSAPP US
            </button>
            <button 
              onClick={handlePhoneClick}
              className="border border-blue-900 text-blue-900 px-8 py-4 rounded-sm text-sm tracking-widest font-semibold hover:bg-blue-50 transition-colors flex items-center justify-center gap-2"
            >
              <FaPhoneAlt size={14} /> CALL US
            </button>
          </div>
        </div>
      </section>
      
      {/* GLOBAL STYLES FOR HIDE SCROLLBAR */}
      <style dangerouslySetInnerHTML={{__html: `
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}} />
    </div>
  );
};

export default Faq;
